import { ref, watch, onMounted, type Ref } from 'vue'
import { useUserStore } from '@/stores/user/userStore'
import { canModify } from '@/utils/userUtils'
import type { IEvent } from '@/types'

type PermissionScope = 'league-admin' | 'team-admin' | 'event-admin' | 'simple'

/**
 * Composable pro kontrolu editačních oprávnění na event.
 *
 * Režimy:
 *  - 'simple'      → FE check: coach / administrator role
 *  - 'league-admin' → BE check: league-admin / global-admin v kontextu liga
 *  - 'team-admin'   → BE check: team-admin / global-admin v kontextu tým
 *  - 'event-admin'  → BE check: event-admin + fallback na team-admin
 */
export function useEventPermissions(
  eventRef: Ref<IEvent>,
  scope: PermissionScope = 'simple',
) {
  const userStore = useUserStore()
  const canEditEvent = ref(false)

  const refresh = async () => {
    try {
      const event = eventRef.value
      if (!event) { canEditEvent.value = false; return }

      const roles = userStore.user?.userRoles || []
      const isGlobalAdmin = roles.includes('administrator') || roles.includes('global-admin')
      if (isGlobalAdmin) {
        canEditEvent.value = true
        return
      }

      // === FE zkratka (simple) ===
      if (scope === 'simple') {
        canEditEvent.value = roles.includes('coach') || roles.includes('administrator') || roles.includes('global-admin')
        return
      }

      // === League-admin scope ===
      if (scope === 'league-admin') {
        const leagueId = getLeagueId(event)
        if (!leagueId) { canEditEvent.value = false; return }
        canEditEvent.value = await canModify({
          requiredOneOfRole: ['league-admin', 'global-admin'],
          leagueId,
        })
        return
      }

      // === Event-admin scope (+ fallback team-admin) ===
      if (scope === 'event-admin') {
        const isEventAdmin = await canModify({
          requiredOneOfRole: ['event-admin'],
          eventId: event._id || null,
        })
        if (isEventAdmin) { canEditEvent.value = true; return }

        const teamId = getTeamId(event._team)
        if (teamId) {
          canEditEvent.value = await canModify({
            requiredOneOfRole: ['team-admin', 'global-admin'],
            teamId,
          })
          return
        }
        canEditEvent.value = false
        return
      }

      // === Team-admin scope ===
      if (scope === 'team-admin') {
        const teamId = getTeamId(event._team)
        if (!teamId) { canEditEvent.value = false; return }
        canEditEvent.value = await canModify({
          requiredOneOfRole: ['team-admin', 'global-admin'],
          teamId,
        })
        return
      }

      canEditEvent.value = false
    } catch (e) {
      console.error('useEventPermissions error:', e)
      canEditEvent.value = false
    }
  }

  // Helpers
  const getLeagueId = (ev: any): string | null => {
    const cand =
      ev?.meta?.leagueId ||
      ev?._league?._id ||
      ev?._league?.id ||
      (typeof ev?._league === 'string' ? ev._league : null)
    return cand ? String(cand) : null
  }

  const getTeamId = (v: any): string | null => {
    const id = typeof v === 'string' ? v : v?._id || v?.id || v?.teamId || null
    return id ? String(id) : null
  }

  // Lifecycle + watchers
  onMounted(refresh)
  watch(
    () => [
      eventRef.value?._id,
      getLeagueId(eventRef.value),
      getTeamId((eventRef.value as any)?._team),
      userStore.user?._id,
      (userStore.user?.userRoles || []).join('|'),
    ],
    refresh,
  )

  return { canEditEvent }
}
